import { FC } from 'react';

import { useTabs } from '../../../../../hooks/Tabs';
import Block from '../../../../../components/Block';
import Column from '../../../../../components/Column';
import TabPanel from '../../../../../components/TabPanel';

import { Campaign, CampaignAdGroup, CampaignFlight } from '../../../../../types/Campaign';
import CampaignAdGroupTabs, { PAGE_TABS } from './CampaignAdGroupTabs';
import CampaignAdGroupCreativeTab from '../Tab/CampaignAdGroupCreativeTab';
import CampaignAdGroupOverviewTab from '../Tab/CampaignAdGroupOverviewTab';
import CampaignAdGroupTargetingTab from '../Tab/CampaignAdGroupTargetingTab';
import moment from 'moment-timezone';
import LinkedFlightsDataGrid from '../DataGrid/LinkedFlightsDataGrid';

interface CampaignAdGroupTabPanelsProps {
    campaignAdGroup: CampaignAdGroup;
    campaign: Campaign;
}

const CampaignAdGroupTabPanels: FC<CampaignAdGroupTabPanelsProps> = ({
    campaignAdGroup,
    campaign,
}): React.JSX.Element => {
    const tabs = useTabs();
    const { activeTab, handleChangeTab } = tabs;

    const getLinkedFlights = (): CampaignFlight[] => {
        if (!campaign.flights) return [];

        const adGroupStart = moment.tz(campaignAdGroup.startDateTime, 'America/New_York').startOf('day');
        const adGroupEnd = moment.tz(campaignAdGroup.endDateTime, 'America/New_York').endOf('day');

        return campaign.flights.filter((flight) => {
            const flightStart = moment.tz(flight.startDateTime, 'America/New_York').startOf('day');
            const flightEnd = moment.tz(flight.endDateTime, 'America/New_York').endOf('day');

            // Check if flight dates overlap with ad group dates
            return (
                (flightStart.isSameOrAfter(adGroupStart) && flightStart.isSameOrBefore(adGroupEnd)) ||
                (flightEnd.isSameOrAfter(adGroupStart) && flightEnd.isSameOrBefore(adGroupEnd)) ||
                (flightStart.isSameOrBefore(adGroupStart) && flightEnd.isSameOrAfter(adGroupEnd))
            );
        });
    };

    const linkedFlights = getLinkedFlights();

    return (
        <Column gap={0}>
            <Block
                disableGutters
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 3,
                }}
            >
                <CampaignAdGroupTabs value={activeTab} onChange={handleChangeTab} />
            </Block>

            <Block disableGutters sx={{ px: 3, pb: 4 }}>
                <TabPanel index={PAGE_TABS.CAMPAIGN_AD_GROUP_OVERVIEW} value={activeTab}>
                    <CampaignAdGroupOverviewTab campaignAdGroup={campaignAdGroup} />
                </TabPanel>
                <TabPanel index={PAGE_TABS.CAMPAIGN_AD_GROUP_FLIGHTS} value={activeTab}>
                    <LinkedFlightsDataGrid flights={linkedFlights} allFlights={campaign.flights || []} />
                </TabPanel>
                <TabPanel index={PAGE_TABS.CAMPAIGN_AD_GROUP_TARGETING} value={activeTab}>
                    <CampaignAdGroupTargetingTab campaignAdGroup={campaignAdGroup} />
                </TabPanel>
                <TabPanel index={PAGE_TABS.CAMPAIGN_AD_GROUP_CREATIVES} value={activeTab}>
                    <CampaignAdGroupCreativeTab campaignAdGroup={campaignAdGroup} />
                </TabPanel>
            </Block>
        </Column>
    );
};

export default CampaignAdGroupTabPanels;
